import {
  FilterBy,
  ProvisioningRequest,
} from '@agilelab/plugin-wb-builder-backend';
import {
  BranchesAndCommitUids,
  CatalogInfoFile,
  DagStatus,
  deconstructVersion,
  DeployStatusWithResult,
  ComputeSkeletonEntityUpdatesRequest,
  ComputeSkeletonEntityUpdatesResponse,
  EntityAndBranch,
  GetProvisioningStatusResponse,
  Log,
  PaginatedProvisioningPlansResponse,
  ProvisioningPlan,
  ProvisioningPreviewComponentResponse,
  ProvisioningPreviewResponse,
  ReleaseAndDeployments,
  ReleaseEntity,
  ReverseProvisioningPlansAndMerged,
  ReverseProvisioningStatus,
  TaskAction,
  TerminationMode,
  ValidationErrorDescriptor,
  ValidationResponse,
  ValidationResponseStatus,
  WitboostEntity,
  FetchEntityCatalogDataRequest,
  FetchEntityCatalogDataResponse,
  GetReverseProvisioningUpdatesRequest,
  GetReverseProvisioningUpdatesResponse,
} from '@agilelab/plugin-wb-builder-common';
import { SkeletonParameters } from '@agilelab/plugin-wb-catalog-extension-common';
import { CustomAlertApi } from '@agilelab/plugin-wb-platform';
import {
  encodeQueryParams,
  handleFailedResponse,
} from '@agilelab/plugin-wb-platform-common';
import {
  DEFAULT_NAMESPACE,
  Entity,
  parseEntityRef,
} from '@backstage/catalog-model';
import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { isBefore, parseISO } from 'date-fns';
import * as yaml from 'yaml';
import {
  BuildDescriptorFromCatalogDataRequest,
  BuildDescriptorFromCatalogDataResponse,
} from './apis/models/descriptors';
import {
  RenderSkeletonRequest,
  RenderSkeletonResponse,
} from './apis/models/skeleton';

export declare type PanelCatalogRequestOptions = {
  token?: string;
};

export type NewVersion = {
  projectName: string;
  version: string;
};

export type ExecuteReverseProvisioningRequest = {
  useCaseTemplateId: string;
  infrastructureTemplateIds: string[];
  environment: string;
  catalogData:
    | {
        type: 'skeleton';
        parameters: SkeletonParameters['parameters'];
        environmentParameters?: SkeletonParameters['environmentParameters'];
        environmentSpecificData?: Record<string, any>;
        parsedCatalogInfo?: Record<string, any>;
      }
    | {
        type: 'legacy';
        catalogInfo: Record<string, any>;
        environmentSpecificData?: Record<string, any>;
      };
  params?: Record<string, any>;
};

export interface PanelCatalogApi {
  validateDescriptor(
    descriptor: string,
    projectName: string,
    projectKind?: string,
  ): Promise<ValidationResponse>;

  validateDescriptorAsync(
    descriptor: string,
    projectName: string,
    projectKind?: string,
  ): Promise<string>;

  /**
   * Retrieves the execution status of a validation operation
   *
   * @param token operation identifier
   * @param includeValidationSummary whether to include the full validation summary in the response (default: `false`)
   */
  getValidateDescriptorStatus(
    token: string,
    includeValidationSummary?: boolean,
  ): Promise<ValidationResponseStatus>;

  fetchPreviewDescriptor(
    projectName: string,
    environment: string,
    kind?: string,
    entitiesAndBranches?: EntityAndBranch[],
    version?: string,
    byPassCache?: boolean,
    hideComponents?: boolean,
  ): Promise<{
    descriptor: string;
    validationError: ValidationErrorDescriptor;
    timestamp?: number;
    hideComponents?: boolean;
  }>;

  buildDescriptorFromCatalogData(
    request: BuildDescriptorFromCatalogDataRequest,
  ): Promise<BuildDescriptorFromCatalogDataResponse>;

  fetchCatalogInfo(
    component: Entity,
    branch?: string,
    env?: string,
    includeSkeletonInfo?: boolean,
  ): Promise<CatalogInfoFile>;

  /**
   * Retrieves the catalog data of an entity
   */
  fetchEntityCatalogData(
    request: FetchEntityCatalogDataRequest,
  ): Promise<FetchEntityCatalogDataResponse>;

  /**
   * Pushes a component's catalog info to its own host git repository
   * @param entity of witboost
   * @param content new catalog info file content
   * @param commitMessage
   * @param targetBranch it has to be an existing branch. default: branch of the component's catalog info
   * @param headCommit id of the commit from which to start the push operation.
   * @param options
   * @throws {GitRepositoryError} if the catalog info file has been modified on `targetBranch` after `headCommit`
   */
  pushCatalogInfo(
    entity: WitboostEntity,
    content: string,
    commitMessage: string,
    targetBranch?: string,
    headCommit?: string,
    filePath?: string,
  ): Promise<void>;

  executeReverseProvisioning(
    request: ExecuteReverseProvisioningRequest,
  ): Promise<string>;

  fetchReleasePreviewDescriptor(
    releaseEntityName: string,
    environment: string,
  ): Promise<string>;

  fetchReleases(
    projectName: string,
    filterBy?: FilterBy,
  ): Promise<ReleaseAndDeployments[]>;

  createSnapshot(
    projectName: string,
    projectKind?: string,
    entitiesAndBranches?: EntityAndBranch[],
  ): Promise<ReleaseEntity>;

  commitSnapshot(
    projectName: string,
    projectKind: string,
    entitiesAndBranches?: EntityAndBranch[],
  ): Promise<ReleaseEntity>;

  promoteToRelease(
    releaseEntityName: string,
    projectName: string,
    projectKind?: string,
  ): Promise<ReleaseEntity>;

  deployRelease(
    releaseEntityName: string,
    projectName: string,
    projectKind: string,
    environment: string,
  ): Promise<any>;

  undeployRelease(
    releaseEntityName: string,
    projectName: string,
    projectKind: string,
    environment: string,
  ): Promise<any>;

  getDeployStatus(
    deployId: string,
    alertApi: CustomAlertApi,
  ): Promise<DeployStatusWithResult>;

  getReverseProvisioningStatus(
    reverseProvisioningId: string,
    alertApi: CustomAlertApi,
  ): Promise<ReverseProvisioningStatus>;

  terminateReverseProvisioning(reverseProvisioningId: string): Promise<void>;

  /**
   * @param mergeCatalogInfo whether to return the merged catalog info (default: `true`)
   */
  getReverseProvisioningPlansAndMerged(
    reverseProvisioningId: string,
    catalogInfo: Record<string, any>,
    mergeCatalogInfo?: boolean,
  ): Promise<ReverseProvisioningPlansAndMerged>;

  getReverseProvisioningUpdates(
    request: GetReverseProvisioningUpdatesRequest,
  ): Promise<GetReverseProvisioningUpdatesResponse>;

  getProvisioningPlan(deployId: string): Promise<ProvisioningPlan>;

  /**
   * @param includeDescriptors whether to include the data product descriptor along with other task details
   */
  getProvisioningPlansByEntityIdAndEnvironment(
    dpId: string,
    environment: string,
    includeDescriptors: boolean,
    includeSnapshot?: boolean,
    filters?: {
      version?: string;
      operations?: TaskAction[];
      offset?: number;
      limit?: number;
    },
  ): Promise<PaginatedProvisioningPlansResponse>;

  newEntityVersion(
    projectName: string,
    projectKind: string,
    version: string,
  ): Promise<NewVersion>;

  getLogs(deployId: string, taskId?: string): Promise<Log[]>;

  getDeploymentUnitStatus(
    deploymentUnitId: string,
    environment: string,
    includeDescriptor?: boolean,
  ): Promise<GetProvisioningStatusResponse>;

  terminateProvisioning(
    deploymentUnitId: string,
    environment: string,
    mode: TerminationMode,
  ): Promise<any>;

  getBranches(
    repoUrl: string,
    searchBranch?: string,
  ): Promise<BranchesAndCommitUids>;

  getDeploymentUnitsProvisioningPreview(
    deploymentUnitId: string,
    environment: string,
    provisioningRequest: ProvisioningRequest,
  ): Promise<ProvisioningPreviewResponse>;

  getComponentDeploymentUnitsProvisioningPreview(
    deploymentUnitId: string,
    componentId: string,
    environment: string,
    includeDescriptor: boolean,
  ): Promise<ProvisioningPreviewComponentResponse>;

  partialProvision(
    deploymentUnitId: string,
    environment: string,
    provisioningRequest: ProvisioningRequest,
    unsafe: boolean,
  ): Promise<ProvisioningPreviewResponse>;

  computeSkeletonEntityUpdates(
    request: ComputeSkeletonEntityUpdatesRequest,
  ): Promise<ComputeSkeletonEntityUpdatesResponse>;

  renderSkeletonEntity(
    request: RenderSkeletonRequest,
  ): Promise<RenderSkeletonResponse>;
}

export const panelCatalogApiRef = createApiRef<PanelCatalogApi>({
  id: 'wb-builder-catalog-panel',
});

/**
 * Note that the methods of this client will throw errors in case there is
 * an error generated by internal logics.
 * Handle the errors thrown in the caller logic.
 */
export class PanelCatalogClient implements PanelCatalogApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getProvisioningPlansByEntityIdAndEnvironment(
    dpId: string,
    environment: string,
    includeDescriptors: boolean,
    includeSnapshot?: boolean,
    filters?: {
      version?: string;
      operations?: TaskAction[];
      offset?: number;
      limit?: number;
    },
  ): Promise<PaginatedProvisioningPlansResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');

    const params = new URLSearchParams({
      ['data-product-id']: dpId,
      environments: environment,
      ['include-snapshot']: Boolean(includeSnapshot).toString(),
      ['include-descriptors']: Boolean(includeDescriptors).toString(),
      ...(filters?.version ? { version: filters.version } : {}),
      ...(filters?.operations
        ? { operations: filters.operations.toString() }
        : {}),
      ...(filters?.offset !== undefined
        ? { offset: filters.offset.toString() }
        : {}),
      ...(filters?.limit ? { limit: filters.limit.toString() } : {}),
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/provisioningplan?${params}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async validateDescriptor(
    descriptor: string,
    projectName: string,
    projectKind?: string,
  ): Promise<ValidationResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/dataproducts/${projectName}/validate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          descriptor: JSON.stringify(yaml.parse(descriptor)),
          projectKind: projectKind ?? 'System',
        }),
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async validateDescriptorAsync(
    descriptor: string,
    projectName: string,
    projectKind?: string,
  ): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v2/dataproducts/${projectName}/validate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          descriptor: JSON.stringify(yaml.parse(descriptor)),
          projectKind: projectKind ?? 'System',
        }),
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async getValidateDescriptorStatus(
    token: string,
    includeValidationSummary?: boolean,
  ): Promise<ValidationResponseStatus> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const queryParams = encodeQueryParams({ includeValidationSummary });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v2/validate/${token}/status${queryParams}`,
      {
        headers: {},
      },
    );

    await handleFailedResponse(response);
    return response.json();
  }

  async newEntityVersion(
    projectName: string,
    projectKind: string,
    version: string,
  ): Promise<NewVersion> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const majorVersion = deconstructVersion(version)?.major + 1;

    const queryParams = new URLSearchParams({
      entityName: projectName,
      version: majorVersion.toString(),
      projectKind: projectKind ?? 'System',
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/new_version?${queryParams.toString()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async fetchReleases(
    projectName: string,
    filterBy?: FilterBy,
  ): Promise<ReleaseAndDeployments[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/releases?${new URLSearchParams({
        dataProductName: projectName,
        ...(filterBy ? { filters: JSON.stringify(filterBy) } : {}),
      })}`,
      {
        method: 'GET',
        headers: {},
      },
    );

    await handleFailedResponse(response);

    const items = (await response.json()) as ReleaseAndDeployments[];

    return items.sort((a, b) =>
      isBefore(parseISO(a.metadata.createdAt), parseISO(b.metadata.createdAt))
        ? 1
        : -1,
    );
  }

  async createSnapshot(
    projectName: string,
    projectKind?: string,
    entitiesAndBranches?: EntityAndBranch[],
  ): Promise<ReleaseEntity> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const queryParams = new URLSearchParams({
      dataproductEntityName: projectName,
      projectKind: projectKind ?? 'System',
    });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/releases?${queryParams.toString()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entitiesAndBranches }),
      },
    );

    await handleFailedResponse(response);

    return (await response.json()) as ReleaseEntity;
  }

  async commitSnapshot(
    projectName: string,
    projectKind: string,
    entitiesAndBranches?: EntityAndBranch[],
  ): Promise<ReleaseEntity> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const queryParams = new URLSearchParams({
      name: projectName,
      projectKind: projectKind ?? 'System',
    });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/commit?${queryParams.toString()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entitiesAndBranches }),
      },
    );

    await handleFailedResponse(response);

    return (await response.json()) as ReleaseEntity;
  }

  async promoteToRelease(
    releaseEntityName: string,
    projectName: string,
    projectKind?: string,
  ): Promise<ReleaseEntity> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/releases?${new URLSearchParams({
        releaseEntityName: releaseEntityName,
        dataproductEntityName: projectName,
        projectKind: projectKind ?? 'system',
      })}`,
      {
        method: 'PUT',
        headers: {},
      },
    );

    await handleFailedResponse(response);

    return (await response.json()) as ReleaseEntity;
  }

  async fetchPreviewDescriptor(
    projectName: string,
    environment: string,
    projectKind?: string,
    entitiesAndBranches?: EntityAndBranch[],
    version?: string,
    byPassCache?: boolean,
    hideComponents?: boolean,
  ): Promise<{
    descriptor: string;
    validationError: ValidationErrorDescriptor;
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const queryParams = new URLSearchParams({
      dataProduct: projectName,
      projectKind: projectKind ?? 'System',
      environment,
      version: version ?? '',
    });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/dataproducts/preview?${queryParams.toString()}${
        byPassCache ? '&bypassCache=true' : ''
      }${hideComponents ? '&hideComponents=true' : ''}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entitiesAndBranches }),
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async buildDescriptorFromCatalogData(
    request: BuildDescriptorFromCatalogDataRequest,
  ): Promise<BuildDescriptorFromCatalogDataResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/entities/descriptor/from-catalog-data`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async fetchCatalogInfo(
    entity: Entity,
    branch?: string,
    env?: string,
  ): Promise<CatalogInfoFile> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');

    const queryParams = new URLSearchParams();
    if (branch) queryParams.set('branch', branch);
    if (env) queryParams.set('env', env);

    const response = await this.fetchApi.fetch(
      `${baseUrl}/${entity.kind}/${
        entity.metadata.namespace ?? DEFAULT_NAMESPACE
      }/${entity.metadata.name}/catalog-info?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {},
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async fetchEntityCatalogData(
    request: FetchEntityCatalogDataRequest,
  ): Promise<FetchEntityCatalogDataResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const { kind, namespace, name } = parseEntityRef(request.entityRef);
    const queryParams = encodeQueryParams({
      branch: request.branch,
      parseSkeletonCatalogInfo:
        request.options.parseSkeletonCatalogInfo ?? false,
      parseSkeletonParameters: request.options.parseSkeletonParameters ?? false,
      includeEnvironmentSpecificDataFor:
        request.options.includeEnvironmentSpecificDataFor,
    });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/${kind}/${namespace}/${name}/catalog-data/from-source${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async pushCatalogInfo(
    entity: WitboostEntity,
    content: string,
    commitMessage: string,
    targetBranch?: string,
    headCommit?: string,
    filePath?: string,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const body = JSON.stringify({
      content: content,
      commitMessage: commitMessage,
      targetBranch: targetBranch,
      headCommit: headCommit,
      filePath,
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/${entity.kind}/${
        entity.metadata.namespace ?? DEFAULT_NAMESPACE
      }/${entity.metadata.name}/catalog-info`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body,
      },
    );
    await handleFailedResponse(response);
  }

  async fetchReleasePreviewDescriptor(
    releaseEntityName: string,
    environment: string,
  ): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/releases/preview?${new URLSearchParams({
        releaseEntityName,
        environment,
      })}`,
      {
        method: 'GET',
        headers: {},
      },
    );

    await handleFailedResponse(response);

    return response.text();
  }

  async executeReverseProvisioning({
    useCaseTemplateId,
    infrastructureTemplateIds,
    environment,
    catalogData,
    params,
  }: ExecuteReverseProvisioningRequest): Promise<string> {
    const body = JSON.stringify({
      useCaseTemplateId,
      infrastructureTemplateIds,
      params,
      environment,
      catalogData,
    });
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/reverse-provisioning`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body,
      },
    );

    await handleFailedResponse(response);

    return response.text();
  }

  async deployRelease(
    releaseEntityName: string,
    projectName: string,
    projectKind: string,
    environment: string,
  ): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/releases/${releaseEntityName}/deploy?${new URLSearchParams({
        releaseEntityName,
        projectName,
        environment,
        projectKind,
      })}`,
      {
        method: 'POST',
        headers: {},
      },
    );

    await handleFailedResponse(response);

    return response.text();
  }

  async undeployRelease(
    releaseEntityName: string,
    projectName: string,
    projectKind: string,
    environment: string,
  ): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/releases/${releaseEntityName}/deploy?${new URLSearchParams({
        releaseEntityName,
        projectName,
        environment,
        projectKind,
      })}`,
      {
        method: 'DELETE',
        headers: {},
      },
    );

    return (await response.text()) as string;
  }

  async getDeployStatus(
    deployId: string,
    alertApi: CustomAlertApi,
  ): Promise<DeployStatusWithResult> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/deploy/${deployId}/status`,
      {
        headers: {},
      },
    );
    try {
      await handleFailedResponse(response);
      return await response.json();
    } catch (error) {
      alertApi.post({ error, severity: 'error' });
      return { status: DagStatus.FAILED, result: null };
    }
  }

  async getReverseProvisioningStatus(
    reverseProvisioningId: string,
    alertApi: CustomAlertApi,
  ): Promise<ReverseProvisioningStatus> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/reverse-provisioning/${reverseProvisioningId}/status`,
      {
        headers: {},
      },
    );
    try {
      await handleFailedResponse(response);
      return await response.json();
    } catch (error) {
      alertApi.post({ error, severity: 'error' });
      return { status: DagStatus.FAILED };
    }
  }

  async terminateReverseProvisioning(
    reverseProvisioningId: string,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    await this.fetchApi.fetch(
      `${baseUrl}/reverse-provisioning/${reverseProvisioningId}/terminate`,
      {
        method: 'DELETE',
        headers: {},
      },
    );
  }

  async getReverseProvisioningUpdates({
    reverseProvisioningOperationId: operationId,
    compatibility,
  }: GetReverseProvisioningUpdatesRequest): Promise<GetReverseProvisioningUpdatesResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const queryParams = encodeQueryParams({
      compatibility: JSON.stringify(compatibility),
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/reverse-provisioning/${operationId}/updates${queryParams}`,
      {
        method: 'GET',
        headers: {},
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async getReverseProvisioningPlansAndMerged(
    reverseProvisioningId: string,
    catalogInfo: Record<string, any>,
    mergeCatalogInfo?: boolean,
  ): Promise<ReverseProvisioningPlansAndMerged> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/reverse-provisioning/${reverseProvisioningId}/plans`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ catalogInfo, mergeCatalogInfo }),
      },
    );
    try {
      await handleFailedResponse(response);
      return await response.json();
    } catch (error) {
      return { environment: '', dag: { status: DagStatus.FAILED } };
    }
  }

  async getProvisioningPlan(deployId: string): Promise<ProvisioningPlan> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/deploy/${deployId}`,
      {
        headers: {},
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async getLogs(deployId: string, taskId?: string): Promise<Log[]> {
    const searchParams = new URLSearchParams({ ...(taskId ? { taskId } : {}) });
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/logs/${deployId}?${searchParams}`,
      {
        headers: {},
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async getDeploymentUnitStatus(
    deploymentUnitId: string,
    environment: string,
    includeDescriptor?: boolean,
  ): Promise<GetProvisioningStatusResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const searchParams = new URLSearchParams({
      ...(includeDescriptor
        ? { ['include-descriptor']: String(includeDescriptor), environment }
        : { environment }),
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/deployment-units/${deploymentUnitId}/provisioning-status?${searchParams}`,
      {
        headers: {},
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async getBranches(
    repoUrl: string,
    searchBranch?: string,
  ): Promise<BranchesAndCommitUids> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/branches?${new URLSearchParams({
        repoUrl: repoUrl,
        ...(searchBranch ? { searchBranch } : {}),
      })}`,
      {
        method: 'GET',
        headers: {},
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async getDeploymentUnitsProvisioningPreview(
    deploymentUnitId: string,
    environment: string,
    provisioningRequest: ProvisioningRequest,
  ): Promise<ProvisioningPreviewResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const searchParams = new URLSearchParams({ environment });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/deployment-units/${deploymentUnitId}/provisioning/preview?${searchParams}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(provisioningRequest),
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async getComponentDeploymentUnitsProvisioningPreview(
    deploymentUnitId: string,
    componentId: string,
    environment: string,
    includeDescriptor: boolean,
  ): Promise<ProvisioningPreviewComponentResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const searchParams = new URLSearchParams({
      environment,
      ['include-descriptor']: String(includeDescriptor),
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/deployment-units/${deploymentUnitId}/${componentId}/provisioning/preview?${searchParams}`,
      {
        headers: {},
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async terminateProvisioning(
    deploymentUnitId: string,
    environment: string,
    mode?: TerminationMode,
  ): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/deployment-units/${deploymentUnitId}/provisioning?${new URLSearchParams(
        {
          environment,
          ...(mode ? { mode } : {}),
        },
      )}`,
      {
        method: 'DELETE',
        headers: {},
      },
    );
    return (await response.text()) as string;
  }

  async partialProvision(
    deploymentUnitId: string,
    environment: string,
    provisioningRequest: ProvisioningRequest,
    unsafe: boolean = false,
  ): Promise<ProvisioningPreviewResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const searchParams = new URLSearchParams({
      environment,
      unsafe: String(unsafe),
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/deployment-units/${deploymentUnitId}/provisioning?${searchParams}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(provisioningRequest),
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async computeSkeletonEntityUpdates(
    request: ComputeSkeletonEntityUpdatesRequest,
  ): Promise<ComputeSkeletonEntityUpdatesResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/skeleton-entities/updates-preview/computation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }

  async renderSkeletonEntity(
    request: RenderSkeletonRequest,
  ): Promise<RenderSkeletonResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/skeleton-entities/rendering-preview/computation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );

    await handleFailedResponse(response);

    return response.json();
  }
}
